/**
 * 다세대 요금 정산 — 공통 포맷 유틸 (작업 문서 §10 공통 규칙)
 */

/** 요금 정산 UI 공통 폰트 (SCSS $stl-font·ECharts와 동일) */
export const SETTLEMENT_FONT_FAMILY = "Roboto Slab";

/** ECharts textStyle 기본값 */
export function stlChartTextStyle(extra = {}) {
    return { fontFamily: SETTLEMENT_FONT_FAMILY, ...extra };
}

/** 금액: 1,234,567 (콤마) */
export function fmtWon(v) {
    if (v === null || v === undefined || isNaN(Number(v))) return "-";
    return Math.trunc(Number(v)).toLocaleString("ko-KR");
}

/**
 * 금액 입력란 표시용 — null/빈값이면 "", 아니면 세 자리 콤마
 * (type=text 입력 UI용; API에는 parseWonInput 결과 숫자만 전달)
 */
export function formatWonInput(v) {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(String(v).replace(/[^\d-]/g, ""));
    if (!Number.isFinite(n)) return "";
    return Math.trunc(Math.abs(n)).toLocaleString("ko-KR");
}

/** 콤마·공백 제거 후 정수 파싱. 빈 입력이면 null */
export function parseWonInput(raw) {
    if (raw === null || raw === undefined) return null;
    const digits = String(raw).replace(/[^\d]/g, "");
    if (digits === "") return null;
    const n = Math.trunc(Number(digits));
    return Number.isFinite(n) ? n : null;
}

/**
 * toFixed 결과에서 소수부 trailing zero만 제거.
 * (구) /\.?0+$/ 는 "1680"처럼 정수 끝 0까지 지워 168만원으로 깨짐.
 */
function stripTrailingFractionalZeros(s) {
    if (!String(s).includes(".")) return String(s);
    return String(s).replace(/0+$/, "").replace(/\.$/, "");
}

/** 금액 compact — primary: 738만원, secondary: 7,382,910 */
export function fmtWonCompact(v) {
    if (v === null || v === undefined || isNaN(Number(v))) {
        return { primary: "-", secondary: null };
    }
    const n = Math.trunc(Number(v));
    const abs = Math.abs(n);
    const full = n.toLocaleString("ko-KR");
    if (abs >= 100000000) {
        const eok = stripTrailingFractionalZeros(
            (n / 100000000).toFixed(abs >= 1000000000 ? 1 : 2),
        );
        return { primary: `${eok}억원`, secondary: full };
    }
    if (abs >= 10000) {
        const man = stripTrailingFractionalZeros(
            (n / 10000).toFixed(abs >= 1000000 ? 0 : 1),
        );
        return { primary: `${man}만원`, secondary: full };
    }
    return { primary: `${full}원`, secondary: null };
}

/** 전월 대비 증감률 (%) — null이면 표시 없음 */
export function fmtDeltaPct(v) {
    if (v === null || v === undefined || isNaN(Number(v))) return "—";
    const n = Number(v);
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(1)}%`;
}

/** 'YYYYMM' → 'YYYY-MM' */
export function regYmToYm(regYm) {
    const s = String(regYm || "").replace(/\D/g, "").slice(0, 6);
    if (s.length < 6) return "";
    return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
}

/** kWh: 소수 1자리 */
export function fmtKwh(v) {
    if (v === null || v === undefined || isNaN(Number(v))) return "-";
    return Number(v).toLocaleString("ko-KR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** 사용량: 단위 무관(kWh/Nm³/MJ 등), 소수 1자리 */
export function fmtUsage(v) {
    return fmtKwh(v);
}

/** 사용량/과금 단위 표시 라벨 (Nm3 → Nm³ 등) */
export function unitLabel(unit) {
    if (!unit) return "kWh";
    if (unit === "Nm3") return "Nm³";
    return unit;
}

/** 면적: 소수 2자리 */
export function fmtArea(v) {
    if (v === null || v === undefined || isNaN(Number(v))) return "-";
    return Number(v).toLocaleString("ko-KR", { maximumFractionDigits: 2 });
}

/** %: 부호 + 소수 1자리 */
export function fmtPct(v) {
    if (v === null || v === undefined || isNaN(Number(v))) return "-";
    const n = Number(v);
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(1)}%`;
}

/** 'YYYYMM' → 'YYYY-MM' */
export function fmtYm(regYm) {
    const s = String(regYm || "").replace(/\D/g, "");
    if (s.length < 6) return regYm || "-";
    return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
}

/** 'YYYYMMDD' → 'YYYY-MM-DD' */
export function fmtDate(yyyymmdd) {
    const s = String(yyyymmdd || "").replace(/\D/g, "");
    if (s.length < 8) return yyyymmdd || "-";
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}

/** 'YYYY-MM' (input[type=month]) → 'YYYYMM' */
export function ymToRegYm(ym) {
    return String(ym || "").replace(/\D/g, "").slice(0, 6);
}

/** 'YYYYMM' ± n개월 */
export function shiftRegYm(regYm, delta) {
    const s = String(regYm).replace(/\D/g, "");
    const y = parseInt(s.slice(0, 4), 10);
    const m = parseInt(s.slice(4, 6), 10);
    const total = y * 12 + (m - 1) + delta;
    const ny = Math.floor(total / 12);
    const nm = (total % 12) + 1;
    return `${ny}${String(nm).padStart(2, "0")}`;
}

/** 당월 'YYYYMM' */
export function currentRegYm() {
    const now = new Date();
    return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** 정산 상태 라벨 (UI용 한글) */
export function statusLabel(status) {
    switch (status) {
        case "DRAFT": return "임시저장";
        case "FINAL": return "확정";
        case "CANCELLED": return "취소됨";
        case "PREVIEW": return "계산됨";
        default: return "미정산";
    }
}

/** 정산 상태 뱃지 클래스 */
export function statusClass(status) {
    switch (status) {
        case "DRAFT": return "stl-badge stl-badge-draft";
        case "FINAL": return "stl-badge stl-badge-final";
        case "CANCELLED": return "stl-badge stl-badge-cancelled";
        case "PREVIEW": return "stl-badge stl-badge-preview";
        default: return "stl-badge stl-badge-none";
    }
}

/** 정산 UI 공통 라벨 (SSOT) */
export const SETTLEMENT_LABELS = {
    totalAmount: "총요금",
    usageShare: "사용량 배분",
    usageShareSum: "사용량 배분 합",
    areaShare: "공용부 배분",
    areaSharePool: "공용부 배분 풀",
    remainder: "잔차",
    householdBillingTotal: "세대 청구 합계",
    totalEntered: "총요금 입력됨",
    monthMatrix: "월별 세대 내역",
    monthMatrixLoading: "월별 세대 내역 조회 중...",
    remainderZero: "잔차 없음",
    remainderCheck: "확인 필요",
    verifyStripGas: "계량 검증 · 전체 = 세대",
    verifyStripElec: "계량 검증 · 전체 = 세대 + 공용",
    reconcileHouseholdPt: "호별 관제점 사용량",
    kepcoCustNo: "한전 고객번호",
    commonMetering: "공용부 계량",
    settlementTotal: "정산 총량",
    compositeVsKepco: "정산 총량 대비",
    realtimeVsSnapshot: "실시간 대비",
    notIndependentMeter: "합성 정의(독립 계량점 아님)",
    ptUnmapped: "관제점 미매핑",
    csv: {
        regYm: "청구월",
        roomName: "호",
        status: "상태",
        usageQty: "사용량",
        usageShareAmount: "사용량 배분",
        areaShareAmount: "공용부 배분",
        manualAdjustAmount: "조정",
        totalAmount: "합계",
        exclusiveArea: "전용면적(㎡)",
    },
    chart: {
        totalAmount: "총요금",
        areaShareAmount: "공용부 배분",
        totalUsage: "총 사용량",
        householdUsage: "세대 사용량",
        commonUsage: "공용부 사용량",
    },
};

/** 테이블 tfoot — N세대 합계 행 */
export function householdCountFooterLabel(n) {
    return `합계 ${n}세대`;
}

/** 세대 청구 합계 − 총요금 툴팁 */
export function remainderDiffTooltip(householdSum, totalAmount, diff) {
    return `세대 청구 합계(${fmtWon(householdSum)}) − 총요금(${fmtWon(totalAmount)}) = ${fmtWon(diff)}원`;
}

/** 상태 스트립 — 계량 검증 라벨 */
export function verifyStripLabel(isGas, isWater) {
    return isGas || isWater ? SETTLEMENT_LABELS.verifyStripGas : SETTLEMENT_LABELS.verifyStripElec;
}

/** 상태 스트립 — 계량 검증 수식 (한글) */
export function verifyFormulaText(us, { isGas, isWater, unit }) {
    if (!us) return "";
    const m = Number(us.masterUsageQty || 0);
    const s = Number(us.subUsageSumQty || 0);
    const c = Number(us.commonUsageQty || 0);
    if (isGas || isWater) {
        const diff = m - s;
        return `전체 − 세대 = ${fmtUsage(diff)} ${unit}`;
    }
    const diff = m - (s + c);
    return `전체 − (세대 + 공용) = ${fmtUsage(diff)} ${unit}`;
}

/** settlementRole 라벨 (ScopeTreePreview 등) */
export const ROLE_META = {
    SITE_ROOT: { label: "단지" },
    BUILDING: { label: "동" },
    HOUSEHOLD: { label: "세대" },
    COMMON_AREA: { label: "공용" },
    ENERGY_EQUIP: { label: "설비" },
    IGNORE: { label: "제외" },
};

/** API 에러 → { errcode, message } */
export function parseApiError(err) {
    const data = err?.response?.data || {};
    return {
        status: err?.response?.status ?? null,
        errcode: data.errcode || data.errno || null,
        message: data.detail || data.message || err?.message || "처리 중 오류가 발생했습니다.",
    };
}

/** 호명 숫자 기준 정렬 (101호, 102호 …) */
export function roomNameSortKey(roomName) {
    const m = String(roomName || "").match(/(\d+)/);
    return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
}

export function sortHouseholdsByRoom(households) {
    return [...(households || [])].sort((a, b) => {
        const ka = roomNameSortKey(a.roomName);
        const kb = roomNameSortKey(b.roomName);
        if (ka !== kb) return ka - kb;
        return String(a.roomName || "").localeCompare(String(b.roomName || ""), "ko");
    });
}
